
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { enWebPage } from "../../../lib/ConstCommand";
import { enDrawingMode } from "../../../lib/classTalk/CodeEnum";

import { subscribe, unsubscribe, isLiveAutoReconnectBlocked, resetLiveReconnect, retryLiveReconnect, LIVE_RECONNECT_STATE_EVENT } from "../../../lib/MediaSoupClient";
import { addMediaError, videoErrorCodeToString } from "../../../lib/MediaErrorLog"; // 20260904 선생님/학생 영상 재생 실패를 누적 오류 로그에 기록 ... by claude
import { initHandsUpFunc, startHandsUpMode, stopHandsUpMode, 
         setHandsUpActive, setHandsUpInactive, clearHandsUpMode, initialize, 
         isIOSWebView} from "../../../lib/HandsUpClient";
import { connectLive, disconnectLive, disconnectLiveForQRLogin, initMyLiveInfo, xmitCmd_IosMessage } from "../../../lib/RabbitmqClient";
import { callExitFullScreen/* , callRequestFullScreen */ } from "../../../lib/func/ExportFunction";

import LiveSmallGroupWindow from "./LiveSmallGroupWindow";
import { initFuncProc } from "../../../lib/rbmq/rcvMsgFromRbmq";

// import ConnectImg from "../../../images/connect.png";
// import AndroidConnectImg from "../../../images/android_connect.png";

const calculateDPI = () => {
    // devicePixelRatio를 사용하여 DPI 계산
    // 일반적으로 1 devicePixelRatio = 96 DPI
    const pixelRatio = window.devicePixelRatio || 1;
    return Math.round(96 * pixelRatio);
}

const ConnectImgComp = styled.img`
    width: '100px';
    height: '100px';
`;

const VideoComp = ConnectImgComp.withComponent("video");

// 20260929 선생님 영상 자동 재연결이 모두 실패해 막힌 경우에는 [다시 연결] 전까지 자동 접속하지 않는다.
const connectLiveIfAllowed = (liveSeq, teacherSeq, teacherInfo) => {
    if (isLiveAutoReconnectBlocked()) {
        console.log("connectLiveIfAllowed - live auto reconnect is blocked. wait for manual retry.");
        return;
    }
    connectLive(liveSeq, teacherSeq, teacherInfo);
}

const isAndroid = navigator.userAgent.toLowerCase().indexOf("android") > -1;
const isMobile = (navigator.userAgent.toLowerCase().indexOf("android") > -1 || navigator.userAgent.toLowerCase().indexOf("iphone") > -1 || (navigator.userAgent.toLowerCase().indexOf("macintosh") > -1 && navigator.maxTouchPoints > 2));
const screenWidth = () => {
    // console.log("screenWidth - window.innerWidth[ " + window.innerWidth + " ]");
    return window.innerWidth;
};

const LiveMediaSoupRcv = ({ isMixedClassMember, userSeq, userLoginPageType, drawingMode, performLiveInfo, performLiveStatusInfo, performHandsUpStatusInfo, smallGroupInfo, teacherViewVisibility, myCamViewVisibility, isRbmqServerConnected, isReadyLiveConsume, isRetryConnect, isFullScreenMode, viewKind, isGuestYn, broadcastOpenYN, isLiveGuestAlertModalOpened, isLiveMixedAlertModalOpened, handleSelectedLiveFunc, handleControlToolFunc }) => {
    const { t } = useTranslation();

    const lDrawingMode = useRef(drawingMode);

    const interval = useRef(null);
    const volumeInterval = useRef(null);

    const [teacherInfo, setTeacherInfo] = useState(performLiveInfo && performLiveInfo.teacher_info ?
        performLiveInfo.teacher_info : { userSeq: -1, userNickname: "", userProfile: "", profileImgUrl: "", userGateWay: "" });

    // const [teacherInfo, setTeacherInfo] = useState({ userSeq: -1, userNickname: "", userProfile: "", profileImgUrl: "", userGateWay: "" });

    const [lWidth, setWidth] = useState(isMobile && screenWidth() < 1000 ? '160px' : '240px');
    const [lHeight, setHeight] = useState(isMobile && screenWidth() < 1000 ? '90px' : '135px');
    const [lHandsUpWidth, setHandsUpWidth] = useState(isMobile && screenWidth() < 1000 ? '160px' : '240px');
    const [lHandsUpHeight, setHandsUpHeight] = useState(isMobile && screenWidth() < 1000 ? '90px' : '135px');

    const [lHandsUpLeftOffset, setHandsUpLeftOffset] = useState(isMobile && screenWidth() < 1000 ? '162px' : '241px');

    const [lHandsUpVideoDisplayMode, setHandsUpVideoDisplayMode] = useState('flex');
    const [lHandsUpImgDisplayMode, setHandsUpImgDisplayMode] = useState('none');

    const [isMyCamFullScreenMode, setIsMyCamFullScreenMode] = useState(false);

    // handsUp이 videoEl / imgEl 중 어떤 것을 사용 중인지 판별용으로 사용 ... by hjkim 20221109
    const isHandsUpImgFlex = useRef(false);

    const videoEl = useRef(null);
    const [teacherPausedIOS, setTeacherPausedIOS] = useState(false); // 20261103 [iOS] 선생님 영상이 다른 앱 소리 등으로 멈춤 → '터치하면 다시 재생' 안내 표시
    const otherAudioBusyRef = useRef(false);   // 20261104 [iOS] 다른 앱(유튜브 PiP 등)이 소리 사용 중인지 (앱 pro-otherAudioState, 모르면 false)
    const lastAutoPlayIOSRef = useRef(0);      // 20261104 [iOS] 자동 play() 마지막 시각 (5초에 1번만 - 재생/멈춤 반복 방지)
    const audioEl = useRef(null);
    const micEl = useRef(null);
    const handsupVideoEl = useRef(null);
    const handsupImgEl = useRef(null);
    const myCamVideoEl = useRef(null);

    const teacherViewAreaRef = useRef(null);
    const handsUpAreaRef = useRef(null);

    // 클래스가 종료된 경우 추적용 ... by hjkim 20260309
    const isLiveEndHandledRef = useRef(false);

    // 클래스 안에 있다가 liveSeq가 -1로 바뀐 전이 추적용 ... 20261027 (web-relay R-195)
    const lastLiveSeqRef = useRef(-1);

    // freeze 시점의 선생님 영상 MediaStream 백업용
    // (WebView가 얼려질 때 srcObject 참조가 끊기는 경우를 대비 ... by hjkim 20260702)
    const tchVideoStreamRef = useRef(null);

    useEffect(() => {
        lDrawingMode.current = drawingMode;
    }, [drawingMode]);

    const liveMediaSoupHandler = useCallback(async () => {
        // console.log(`liveMediaSoupHandler - liveSeq[${performLiveInfo.liveSeq}], liveStatus[${performLiveInfo.liveStatus}], connectStatus[${performLiveStatusInfo.connectStatus}]`);
        if ((isLiveGuestAlertModalOpened === false) && (isLiveMixedAlertModalOpened === false)) {
            const isLeftLive = lastLiveSeqRef.current > -1 && performLiveInfo.liveSeq === -1; // 20261027 (web-relay R-195)
            lastLiveSeqRef.current = performLiveInfo.liveSeq; // 20261027 (web-relay R-195)

            if (performLiveInfo.liveSeq > -1 && teacherInfo.userSeq > -1 && teacherInfo.userGateWay && teacherInfo.userGateWay !== "") {
                if (performLiveInfo.liveStatus === "Y") {
                    // ─────────────────────────────────────────────────
                    // liveStatus가 Y가 되면 종료 처리 플래그 리셋
                    // 다음 클래스 종료 시 다시 disconnect가 호출될 수 있도록 함
                    // ─────────────────────────────────────────────────
                    isLiveEndHandledRef.current = false;

                    if (isMixedClassMember || isGuestYn === "Y") {
                        if (performLiveInfo.mixedClassStatus === "Y" || broadcastOpenYN === "Y") { // 믹스드 멤버가 입장할 때
                            if (performLiveStatusInfo.connectStatus === "disable") {
                                console.log(`(1)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                                console.log("(1)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                                console.log("(1)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                                console.log("(1)liveMediaSoupHandler - ", teacherInfo.userSeq, teacherInfo.userNickname, teacherInfo.userProfile, teacherInfo.profileImgUrl, teacherInfo.userGateWay);

                                connectLiveIfAllowed(performLiveInfo.liveSeq, teacherInfo.userSeq, {
                                    userSeq: teacherInfo.userSeq, 
                                    userNickname: teacherInfo.userNickname, 
                                    userProfile: teacherInfo.userProfile, 
                                    profileImgUrl: teacherInfo.profileImgUrl, 
                                    userGateWay: teacherInfo.userGateWay
                                });
                            } else {
                                console.log(`(1-1)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                            }
                        } else { // mixedClassStatus === "N" && broadcastOpenYN === "N" case...
                            if (performLiveStatusInfo.connectStatus !== "disable") { // 믹스드 멤버인데 선생님이 먼저 종료하는 경우
                                if (userLoginPageType !== enWebPage.LiveQRLogin && userLoginPageType !== enWebPage.MixedClassLogin) {
                                    console.log(`(2-1)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                                    console.log("(2-1)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                                    console.log("(2-1)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                                    console.log("(2-1)liveMediaSoupHandler - ", {
                                        userSeq: teacherInfo.userSeq, 
                                        userNickname: teacherInfo.userNickname, 
                                        userProfile: teacherInfo.userProfile, 
                                        profileImgUrl: teacherInfo.profileImgUrl, 
                                        userGateWay: teacherInfo.userGateWay
                                    });
                                    disconnectLive();
                                } else {
                                    console.log(`(2-2)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                                    console.log("(2-2)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                                    console.log("(2-2)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                                    console.log("(2-2)liveMediaSoupHandler - ", {
                                        userSeq: teacherInfo.userSeq, 
                                        userNickname: teacherInfo.userNickname, 
                                        userProfile: teacherInfo.userProfile, 
                                        profileImgUrl: teacherInfo.profileImgUrl, 
                                        userGateWay: teacherInfo.userGateWay
                                    });
                                    if (isIOSWebView()) {
                                        ///await clearHandsUpMode();
                                        // await new Promise(resolve => {
                                        //     window._mediasoupUnpublishResolve = resolve;
                                        //     setTimeout(resolve, 3000);
                                        // }); 
                                    }
                                    unsubscribe(false, true);
                                    disconnectLiveForQRLogin();
                                }
                            } else {
                                console.log(`(2-3)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                            }
                        }
                    } else { // live member가 입장하는 경우 
                        if (performLiveStatusInfo.connectStatus === "disable") {
                            console.log(`(3)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                            console.log("(3)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                            console.log("(3)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                            console.log("(3)liveMediaSoupHandler - ", {
                                userSeq: teacherInfo.userSeq, 
                                userNickname: teacherInfo.userNickname, 
                                userProfile: teacherInfo.userProfile, 
                                profileImgUrl: teacherInfo.profileImgUrl, 
                                userGateWay: teacherInfo.userGateWay
                            });

                            connectLiveIfAllowed(performLiveInfo.liveSeq, teacherInfo.userSeq, {
                                userSeq: teacherInfo.userSeq, 
                                userNickname: teacherInfo.userNickname, 
                                userProfile: teacherInfo.userProfile, 
                                profileImgUrl: teacherInfo.profileImgUrl, 
                                userGateWay: teacherInfo.userGateWay
                            });
                        } else {
                            console.log(`(3-1)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                        }
                    }
                } else { // liveStatus === "N" case ---- 라이브 멤버인데 선생님이 먼저 종료하는 경우
                    if (!isLiveEndHandledRef.current) {
                        isLiveEndHandledRef.current = true; // 중복 호출 방지
                        resetLiveReconnect("live-end"); // 20260929 수업 종료 시 자동 재연결 안내 정리

                        xmitCmd_IosMessage("ct_FinishLive", { endedByTeacher: true });

                        console.log(`(4)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                        console.log("(4)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                        console.log("(4)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                        console.log("(4)liveMediaSoupHandler - ", {
                            userSeq: teacherInfo.userSeq, 
                            userNickname: teacherInfo.userNickname, 
                            userProfile: teacherInfo.userProfile, 
                            profileImgUrl: teacherInfo.profileImgUrl, 
                            userGateWay: teacherInfo.userGateWay
                        });

                        if (userLoginPageType !== enWebPage.LiveQRLogin && userLoginPageType !== enWebPage.MixedClassLogin) {
                            console.log(`(4-1)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                            console.log("(4-1)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                            console.log("(4-1)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                            console.log("(4-1)liveMediaSoupHandler - ", {
                                userSeq: teacherInfo.userSeq, 
                                userNickname: teacherInfo.userNickname, 
                                userProfile: teacherInfo.userProfile, 
                                profileImgUrl: teacherInfo.profileImgUrl, 
                                userGateWay: teacherInfo.userGateWay
                            });
                            disconnectLive();

                            // 선생님 종료 시 Home도 수업 페이지에 머물지 않도록 기존 GO_TO_HOME 사용 ... 20261027 (web-relay R-201)
                            if (userLoginPageType === enWebPage.Home) {
                                handleSelectedLiveFunc({ selectedFunc: "GO_TO_HOME" });
                            }
                        } else {
                            console.log(`(4-2)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                            console.log("(4-2)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                            console.log("(4-2)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                            console.log("(4-2)liveMediaSoupHandler - ", {
                                userSeq: teacherInfo.userSeq, 
                                userNickname: teacherInfo.userNickname, 
                                userProfile: teacherInfo.userProfile, 
                                profileImgUrl: teacherInfo.profileImgUrl, 
                                userGateWay: teacherInfo.userGateWay
                            });
                            if (isIOSWebView()) {
                                //await clearHandsUpMode();
                                // await new Promise(resolve => {
                                //     window._mediasoupUnpublishResolve = resolve;
                                //     setTimeout(resolve, 3000);
                                // }); 
                            }
                            unsubscribe(false, true);
                            disconnectLiveForQRLogin();
                        }
                    } else {
                        // 이미 처리됨 - 중복 방지 로그
                        console.log(`(4-skip)liveMediaSoupHandler - 이미 종료 처리 완료, connectStatus[${performLiveStatusInfo.connectStatus}]`);
                    }
                }
            } else { /** 선생님 클래스 종료 전 학생이 끝내기 버튼을 누르고 나갔을 경우를 위한 처리 by hjkim 20220404 */
                // 선생님 클래스 종료 전 학생이 먼저 나간 경우 처리 (케이스 6)
                if (!isLiveEndHandledRef.current && (performLiveStatusInfo.connectStatus !== "disable" || isLeftLive)) { // 20261027 isLeftLive 추가 (web-relay R-195)
                    isLiveEndHandledRef.current = true;

                    xmitCmd_IosMessage("ct_FinishLive", { endedByTeacher: false });
                    
                    if (userLoginPageType !== enWebPage.LiveQRLogin && userLoginPageType !== enWebPage.MixedClassLogin) {
                        console.log(`(6)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                        console.log("(6)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                        console.log("(6)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                        console.log("(6)liveMediaSoupHandler - ", {
                            userSeq: teacherInfo.userSeq, 
                            userNickname: teacherInfo.userNickname, 
                            userProfile: teacherInfo.userProfile, 
                            profileImgUrl: teacherInfo.profileImgUrl, 
                            userGateWay: teacherInfo.userGateWay
                        });
                        disconnectLive();
                    } else {
                        console.log(`(6-1)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                        console.log("(6-1)liveMediaSoupHandler - ", isMixedClassMember, isGuestYn);
                        console.log("(6-1)liveMediaSoupHandler - ", performLiveInfo.liveSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType);
                        console.log("(6-1)liveMediaSoupHandler - ", {
                            userSeq: teacherInfo.userSeq, 
                            userNickname: teacherInfo.userNickname, 
                            userProfile: teacherInfo.userProfile, 
                            profileImgUrl: teacherInfo.profileImgUrl, 
                            userGateWay: teacherInfo.userGateWay
                        });
                        // if (isIOSWebView()) {
                        //     await clearHandsUpMode();
                        // }
                        unsubscribe(false, true);
                        disconnectLiveForQRLogin();
                    }
                } else {
                    console.log(`(6-2)liveMediaSoupHandler - liveStatus[${performLiveInfo.liveStatus}] connectStatus[${performLiveStatusInfo.connectStatus}]`);
                }
            }
        }
    }, [isLiveGuestAlertModalOpened, isLiveMixedAlertModalOpened, isGuestYn, isMixedClassMember, broadcastOpenYN, teacherInfo.userSeq, teacherInfo.userNickname, teacherInfo.userProfile, teacherInfo.profileImgUrl, teacherInfo.userGateWay, performLiveInfo.mixedClassStatus, performLiveInfo.liveSeq, performLiveInfo.liveStatus, performLiveStatusInfo.connectStatus, userLoginPageType]);

    const liveHandsUpHandler = useCallback(() => {
        // console.log(`liveHandsUpHandler - isGuestYn[${isGuestYn}], broadcastOpenYN[${broadcastOpenYN}], handsUpSelectYN[${performLiveInfo.handsUpSelectYN}]`);
        // console.log(`liveHandsUpHandler - liveSeq[${performLiveInfo.liveSeq}], teacherUserSeq[${teacherInfo.userSeq}], userSeq[${userSeq}]`);
        // console.log(`liveHandsUpHandler - handsUpYN[${performLiveInfo.handsUpYN}], handsUpSelectYN[${performLiveInfo.handsUpSelectYN}], connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
        // console.log(`liveHandsUpHandler - isRbmqServerConnected[${isRbmqServerConnected}], isReadyLiveConsume[${isReadyLiveConsume}], connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
        if ((isLiveGuestAlertModalOpened === false) && (isLiveMixedAlertModalOpened === false) && isRbmqServerConnected && isReadyLiveConsume) {
            if (performLiveInfo.liveSeq > -1 && teacherInfo.userSeq > -1 && userSeq > -1) {
                if (performLiveInfo.liveStatus === "Y") {
                    if (performLiveInfo.handsUpYN === "Y") {
                        if (isMixedClassMember || isGuestYn === "Y") {
                            if (performLiveInfo.mixedClassStatus === "Y" || broadcastOpenYN === "Y") {
                                if (performLiveStatusInfo.isConnect) {
                                    if (performHandsUpStatusInfo.connectStatus !== "connect") {
                                        console.log(`(1)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                                        startHandsUpMode(performLiveInfo.liveSeq, teacherInfo.userSeq, userSeq, performLiveInfo.handsUpSelectYN, handsupVideoEl.current, handsupImgEl.current, myCamVideoEl.current, interval, volumeInterval);//, handsupAudioEl.current);
                                    } else {
                                        console.log(`(1-1)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                                    }
                                } else {
                                    console.log(`(1-2)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                                }
                            } else { // mixedClassStatus === "N" && broadcastOpenYN === "N" case...
                                if (performHandsUpStatusInfo.connectStatus !== "disable") {
                                    console.log(`(2)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                                    stopHandsUpMode(performLiveInfo.liveSeq, userSeq, false, false);
                                } else {
                                    console.log(`(2-1)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                                }
                            }
                        } else { // live member인 case 
                            if (performLiveStatusInfo.isConnect) {
                                if (performHandsUpStatusInfo.connectStatus !== "connect") {
                                    console.log(`(3)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                                    startHandsUpMode(performLiveInfo.liveSeq, teacherInfo.userSeq, userSeq, performLiveInfo.handsUpSelectYN, handsupVideoEl.current, handsupImgEl.current, myCamVideoEl.current, interval, volumeInterval);//, handsupAudioEl.current);
                                } else {
                                    console.log(`(3-1)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                                }
                            } else {
                                console.log(`(3-2)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                            }
                        }
                    } else { // performLiveInfo.handsUpYN === "N" case
                        if (performHandsUpStatusInfo.connectStatus !== "disable") {
                            console.log(`(4)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                            stopHandsUpMode(performLiveInfo.liveSeq, userSeq, false, false);
                        } else {
                            console.log(`(4-1)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                        }
                    }
                } else { // performLiveInfo.liveStatus === "N" case
                    if (performHandsUpStatusInfo.connectStatus !== "disable") {
                        console.log(`(5)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                        stopHandsUpMode(performLiveInfo.liveSeq, userSeq, false, true);
                    } else {
                        console.log(`(5-1)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                    }
                }
            } else { /** 선생님 클래스 종료 전 학생이 끝내기 버튼을 누르고 나갔을 경우를 위한 처리 by hjkim 20220404 */
                if (performHandsUpStatusInfo.connectStatus !== "disable") {
                    console.log(`(8)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                    clearHandsUpMode();
                } else {
                    console.log(`(9)liveHandsUpHandler - liveStatus[${performLiveInfo.liveStatus}] isConnect[${performLiveStatusInfo.isConnect}] handsUpYN[${performLiveInfo.handsUpYN}] connectStatus[${performHandsUpStatusInfo.connectStatus}]`);
                }
            }
        } else if ((isLiveGuestAlertModalOpened === false) && (isLiveMixedAlertModalOpened === false) && performHandsUpStatusInfo.connectStatus !== "disable") { // 20261028 게이트 false여도 실제 종료/퇴장 정리는 실행 (web-relay R-224·R-225)
            if (performLiveInfo.liveSeq > -1 && teacherInfo.userSeq > -1 && userSeq > -1 && performLiveInfo.liveStatus === "N") {
                console.log(`(10)liveHandsUpHandler - 게이트 false, liveStatus[N] → stopHandsUpMode. isRbmqServerConnected[${isRbmqServerConnected}] isReadyLiveConsume[${isReadyLiveConsume}]`);
                stopHandsUpMode(performLiveInfo.liveSeq, userSeq, false, true);
            } else if (performLiveInfo.liveSeq === -1) {
                console.log(`(11)liveHandsUpHandler - 게이트 false, liveSeq[-1] → clearHandsUpMode. isRbmqServerConnected[${isRbmqServerConnected}] isReadyLiveConsume[${isReadyLiveConsume}]`);
                clearHandsUpMode();
            }
        }
    }, [isRbmqServerConnected, isReadyLiveConsume, isLiveGuestAlertModalOpened, isLiveMixedAlertModalOpened, isGuestYn, isMixedClassMember, broadcastOpenYN, userSeq, teacherInfo.userSeq, performLiveInfo.mixedClassStatus, performLiveInfo.liveSeq, performLiveInfo.liveStatus, performLiveStatusInfo.isConnect, performLiveInfo.handsUpYN, performLiveInfo.handsUpSelectYN, performHandsUpStatusInfo.connectStatus]);

    const liveHandsUpActiveHandler = useCallback(() => {
        if (performLiveInfo.liveSeq > -1 && userSeq > -1) {
            if (performLiveInfo.liveStatus === "Y" && performLiveInfo.handsUpYN === "Y" && performHandsUpStatusInfo.connectStatus === "connect") {
                if (performLiveInfo.handsUpSelectYN === "Y") {
                    setHandsUpActive(performLiveInfo.liveSeq, userSeq);
                } else {
                    setHandsUpInactive(performLiveInfo.liveSeq, userSeq);
                }
            }
        }
    }, [performLiveInfo.liveSeq, userSeq, performLiveInfo.liveStatus, performLiveInfo.handsUpYN, performLiveInfo.handsUpSelectYN, performHandsUpStatusInfo.connectStatus]);

    const liveTeacherViewHandler = useCallback(() => {
        if (teacherViewVisibility !== "visible") return;

        if (isFullScreenMode) {
            setWidth('100%');
            setHeight('100%');
            setHandsUpLeftOffset('unset');
        } else {
            setWidth(isMobile && screenWidth() < 1000 ? '160px' : '240px');
            setHeight(isMobile && screenWidth() < 1000 ? '90px' : '135px');
            setHandsUpLeftOffset(isMobile && screenWidth() < 1000 ? '162px' : '241px');
        }
    }, [isFullScreenMode, teacherViewVisibility]);

    const liveMyCamViewHandler = useCallback(() => {
        if (isFullScreenMode) {
            if (isMyCamFullScreenMode) {
                setIsMyCamFullScreenMode(false);
            } else {
                setHandsUpWidth(isMobile && screenWidth() < 1000 ? '160px' : '240px');
                setHandsUpHeight(isMobile && screenWidth() < 1000 ? '90px' : '135px');
                setHandsUpLeftOffset('unset');
            }
        } else {
            if (isMyCamFullScreenMode) {
                if (myCamViewVisibility === "none") {
                    setIsMyCamFullScreenMode(false);
                } else {
                    setHandsUpWidth('100%');
                    setHandsUpHeight('100%');
                    setHandsUpLeftOffset('unset');
                }
            } else {
                setHandsUpWidth(isMobile && screenWidth() < 1000 ? '160px' : '240px');
                setHandsUpHeight(isMobile && screenWidth() < 1000 ? '90px' : '135px');
                setHandsUpLeftOffset(isMobile && screenWidth() < 1000 ? '162px' : '241px');
            }
        }
    }, [isFullScreenMode, isMyCamFullScreenMode, myCamViewVisibility]);

    const imageProcess = (status, value) => {
        switch (status) {
            case "initPAD":
                handleControlToolFunc({ target: "isFullScreenMode", value });
                break;

            case "viewKind_camera": {
                isHandsUpImgFlex.current = false;

                setHandsUpImgDisplayMode('none');
                setHandsUpVideoDisplayMode('flex');

                handleControlToolFunc({ target: "viewKind", value: "CAMERA" });
                handleControlToolFunc({ target: "myCamViewVisibility", value: "none" });

                const changeButton = document.getElementById("btnChangeViewKind");
                if (changeButton) changeButton.disabled = false;

                const userMicButton = document.getElementById("btnChangeMicStatus");
                if (userMicButton) {
                    if (userMicButton.disabled) {
                        console.log("set userMicButton.disabled = false!!");
                        userMicButton.disabled = false;
                    }
                }
            } break;

            case "viewKind_screen": {
                isHandsUpImgFlex.current = false;

                setHandsUpImgDisplayMode('none');
                setHandsUpVideoDisplayMode('flex');

                handleControlToolFunc({ target: "viewKind", value: "SCREEN" });
                handleControlToolFunc({ target: "myCamViewVisibility", value: "none" });

                const changeButton = document.getElementById("btnChangeViewKind");
                if (changeButton) changeButton.disabled = false;
            } break;

            case "mic_disable": {
                const userMicButton = document.getElementById("btnChangeMicStatus");
                if (userMicButton) {
                    console.log("set userMicButton.disabled = true!!");
                    userMicButton.disabled = true;
                }
            } break;

            case "android_appConnect":
                isHandsUpImgFlex.current = true;

                if (handsupImgEl.current !== null) {
                    handsupImgEl.current.src = "/images/android_connect.png";
                }

                setHandsUpVideoDisplayMode('none');
                setHandsUpImgDisplayMode('flex');
                break;

            case "android_appNotConnect":
                isHandsUpImgFlex.current = true;

                if (handsupImgEl.current !== null) {
                    handsupImgEl.current.src = "/images/android_notconnected.png";
                }

                setHandsUpVideoDisplayMode('none');
                setHandsUpImgDisplayMode('flex');
                break;

            case "android_appMicOn": {
                const userMicButton = document.getElementById("btnChangeMicStatus");
                if (userMicButton) {
                    if (userMicButton.disabled) {
                        console.log("set userMicButton.disabled = false!!");
                        userMicButton.disabled = false;
                    }
                }

                handleSelectedLiveFunc({ selectedFunc: "CHANGE_PROC_MIC", data: { status: true } });
            } break;

            case "android_appMicOff": {
                const userMicButton = document.getElementById("btnChangeMicStatus");
                if (userMicButton) {
                    if (userMicButton.disabled) {
                        console.log("set userMicButton.disabled = false!!");
                        userMicButton.disabled = false;
                    }
                }

                handleSelectedLiveFunc({ selectedFunc: "CHANGE_PROC_MIC", data: { status: false } });
            } break;

            case "android_appMic": {
                const userMicButton = document.getElementById("btnChangeMicStatus");
                if (userMicButton) {
                    if (userMicButton.disabled) {
                        console.log("set userMicButton.disabled = false!!");
                        userMicButton.disabled = false;
                    }
                }

                console.log("value => ", value);
                handleSelectedLiveFunc({ selectedFunc: "CHANGE_PROC_MIC", data: { status: value } });
            } break;

            case "hImg_visible":
                isHandsUpImgFlex.current = true;

                setHandsUpVideoDisplayMode('none');
                setHandsUpImgDisplayMode('flex');
                break;

            case "h_disconnect":
                isHandsUpImgFlex.current = true;

                if (handsupImgEl.current !== null) {
                    handsupImgEl.current.src = "/images/connect.png";
                }

                setHandsUpVideoDisplayMode('none');
                setHandsUpImgDisplayMode('flex');

                handleControlToolFunc({ target: "isFullScreenMode", value: false });
                break;

            case "myCam_visible":
                if (isHandsUpImgFlex.current) {
                    setHandsUpVideoDisplayMode('none');
                    setHandsUpImgDisplayMode('none');
                } else {
                    setHandsUpImgDisplayMode('none');
                    setHandsUpVideoDisplayMode('none');
                }

                handleControlToolFunc({ target: "myCamViewVisibility", value: "flex" });
                break;

            case "myCam_invisible":
                if (isHandsUpImgFlex.current) {
                    setHandsUpVideoDisplayMode('none');
                    setHandsUpImgDisplayMode('flex');
                } else {
                    setHandsUpImgDisplayMode('none');
                    setHandsUpVideoDisplayMode('flex');
                }

                handleControlToolFunc({ target: "myCamViewVisibility", value: "none" });
                break;

            case "camera_noLongerAvailable":
                handleSelectedLiveFunc({ selectedFunc: "MEDIA_ALERT", data: { kind: "camera", mediaDevice_info: value } });
                break;

            case "myCam_noLongerAvailable":
                handleSelectedLiveFunc({ selectedFunc: "MEDIA_ALERT", data: { kind: "myCam", mediaDevice_info: value } });
                break;

            default:
                break;
        }
    }

    useEffect(() => {
        initFuncProc(imageProcess);
        initHandsUpFunc(imageProcess);
        initMyLiveInfo(performLiveInfo);

        if (performLiveInfo.liveSeq >= 0 && performLiveInfo.liveStatus === "N") {
            initialize();
        }

        if (isAndroid) {
            let dpi = calculateDPI();
            console.log("dpi => ", dpi);
        }

        return () => {
            callExitFullScreen();

            if (interval.current != null) {
                clearInterval(interval.current)
                interval.current = null;
            }

            if (volumeInterval.current !== null) {
                clearInterval(volumeInterval.current);
                volumeInterval.current = null;
            }
        }
    }, []);

    useEffect(() => {
        console.log("useEffect - performLiveInfo.teacher_info => ", performLiveInfo.teacher_info);
        if (performLiveInfo.teacher_info) {
            setTeacherInfo({
                userSeq: performLiveInfo.teacher_info.userSeq,
                userNickname: performLiveInfo.teacher_info.userNickname,
                userProfile: performLiveInfo.teacher_info.userProfile,
                profileImgUrl: performLiveInfo.teacher_info.profileImgUrl,
                userGateWay: performLiveInfo.teacher_info.userGateWay
            });
        }
    }, [performLiveInfo.teacher_info]);

    useEffect(() => {
        liveMediaSoupHandler();
    }, [liveMediaSoupHandler]);

    useEffect(() => {
        liveHandsUpHandler();
    }, [liveHandsUpHandler]);

    useEffect(() => {
        liveHandsUpActiveHandler();
    }, [liveHandsUpActiveHandler]);

    useEffect(() => {
        liveTeacherViewHandler();
    }, [liveTeacherViewHandler]);

    useEffect(() => {
        liveMyCamViewHandler();
    }, [liveMyCamViewHandler]);

    // 20260929 선생님 영상 자동 재연결 상태(재연결 중 / 실패) 수신
    const [liveReconnectState, setLiveReconnectState] = useState("idle");
    useEffect(() => {
        const onLiveReconnectState = (event) => {
            const state = event && event.detail ? event.detail.state : "idle";
            setLiveReconnectState(state === "reconnecting" || state === "failed" ? state : "idle");
        };
        window.addEventListener(LIVE_RECONNECT_STATE_EVENT, onLiveReconnectState);
        return () => {
            window.removeEventListener(LIVE_RECONNECT_STATE_EVENT, onLiveReconnectState);
            resetLiveReconnect("unmount");
        };
    }, []);

    const onClickLiveReconnect = (e) => {
        e.stopPropagation();
        retryLiveReconnect();
    };

    useEffect(() => {
        if (performLiveStatusInfo.isConnect) {
            subscribe(videoEl.current, audioEl.current, micEl.current);
            handleControlToolFunc({ target: "teacherViewVisibility", value: "visible" });
        }
    }, [performLiveStatusInfo.isConnect]);

    // 20260904 선생님/손들기(학생) 영상의 실제 재생(디코딩) 실패를 감지해 누적 오류 로그에 기록한다. ... by claude
    const teacherInfoRef = useRef(teacherInfo);
    useEffect(() => { teacherInfoRef.current = teacherInfo; }, [teacherInfo]);

    useEffect(() => {
        const videoNode = videoEl.current;
        if (!videoNode) return undefined;

        const onTeacherVideoError = () => {
            const mediaError = videoNode.error;
            addMediaError({
                roomKind: "live",
                mediaKind: "video",
                prodUserSeq: teacherInfoRef.current.userSeq,
                errorCode: videoErrorCodeToString(mediaError && mediaError.code)
            });
        };

        videoNode.addEventListener("error", onTeacherVideoError);
        return () => videoNode.removeEventListener("error", onTeacherVideoError);
    }, []);

    useEffect(() => {
        const handsupVideoNode = handsupVideoEl.current;
        if (!handsupVideoNode) return undefined;

        const onHandsupVideoError = () => {
            const mediaError = handsupVideoNode.error;
            addMediaError({
                roomKind: "handsup",
                mediaKind: "video",
                prodUserSeq: teacherInfoRef.current.userSeq,
                errorCode: videoErrorCodeToString(mediaError && mediaError.code)
            });
        };

        handsupVideoNode.addEventListener("error", onHandsupVideoError);
        return () => handsupVideoNode.removeEventListener("error", onHandsupVideoError);
    }, []);

    useEffect(() => {
        if (performLiveInfo.handsUpYN === "Y" && performHandsUpStatusInfo.connectStatus === "connect") {
            handleControlToolFunc({ target: "teacherViewVisibility", value: "visible" });
        }
    }, [performLiveInfo.handsUpYN, performHandsUpStatusInfo.connectStatus]);

    useEffect(() => {
        if (isRetryConnect) {
            startHandsUpMode(performLiveInfo.liveSeq, teacherInfo.userSeq, userSeq, performLiveInfo.handsUpSelectYN, handsupVideoEl.current, handsupImgEl.current, myCamVideoEl.current, interval, volumeInterval/*, handsupAudioEl.current*/);
            handleControlToolFunc({ target: "isRetryConnect" });
        }
    }, [isRetryConnect]);

    // ─────────────────────────────────────────────────
    // WebView가 백그라운드 → 포그라운드로 복귀할 때(Page Lifecycle API)
    // 정지된 WebRTC 비디오들을 다시 재생시킨다.
    // 네이티브 Activity의 onPause/onResume(WebView 전체 pause/resume)과는
    // 별개로, 브라우저 자체가 페이지를 freeze 시키는 경우를 보완하기 위함.
    // ─────────────────────────────────────────────────
    useEffect(() => {
        const onFreezeDocument = () => {
            console.log("LiveMediaSoupRcv - onFreezeDocument called");

            // 선생님 영상: freeze되기 직전 srcObject를 백업해둔다.
            // (resume 시 srcObject가 유실돼 있으면 이걸로 복원 시도)
            if (videoEl.current && videoEl.current.srcObject) {
                console.log(`onFreezeDocument - teacher video [ muted:${videoEl.current.muted}, paused:${videoEl.current.paused} ]`);
                tchVideoStreamRef.current = videoEl.current.srcObject;
            }
        };

        const onResumeDocument = () => {
            console.log("LiveMediaSoupRcv - onResumeDocument called");

            // 선생님 영상 재개
            if (videoEl.current) {
                // srcObject가 유실된 경우에만 백업본으로 복원 (정상적으로 남아있다면 그대로 사용)
                if (!videoEl.current.srcObject && tchVideoStreamRef.current) {
                    console.log("onResumeDocument - teacher video srcObject 복원");
                    videoEl.current.srcObject = tchVideoStreamRef.current;
                }

                if (videoEl.current.paused) {
                    console.log("onResumeDocument - teacher video play() 호출");
                    videoEl.current.play().catch((err) => {
                        console.warn("teacher video play 실패:", err);
                        // 20260904 alert 없이 조용히 무시되던 재생 실패도 누적 오류 로그에 기록 ... by claude
                        addMediaError({ roomKind: "live", mediaKind: "video", prodUserSeq: teacherInfoRef.current.userSeq, errorCode: "PLAYBACK_FAILED" });
                    });
                }
            }

            // 손들기(HandsUp) 영상 재개 - 학생 캠 화면
            if (handsupVideoEl.current && handsupVideoEl.current.paused) {
                console.log("onResumeDocument - handsup video play() 호출");
                handsupVideoEl.current.play().catch((err) => {
                    console.warn("handsup video play 실패:", err);
                    // 20260904 alert 없이 조용히 무시되던 재생 실패도 누적 오류 로그에 기록 ... by claude
                    addMediaError({ roomKind: "handsup", mediaKind: "video", prodUserSeq: teacherInfoRef.current.userSeq, errorCode: "PLAYBACK_FAILED" });
                });
            }

            // 내 캠(myCam) 영상 재개
            if (myCamVideoEl.current && myCamVideoEl.current.paused) {
                console.log("onResumeDocument - myCam video play() 호출");
                myCamVideoEl.current.play().catch((err) => console.warn("myCam video play 실패:", err));
            }
        };

        document.addEventListener("freeze", onFreezeDocument);
        document.addEventListener("resume", onResumeDocument);

        // 20261103 [iOS] WKWebView(WebKit)는 freeze/resume(Page Lifecycle) 이벤트가 없어 위 복구가 iOS에서 동작하지 않았음
        // → 앱 복귀(visibilitychange/pageshow) 시 같은 onResumeDocument 실행 (유튜브 등 다른 앱 소리 인터럽트로 선생님 영상이 멈춘 경우 복구)
        const isIOS = isIOSWebView();
        const onVisibleIOS = () => {
            if (document.visibilityState === "visible") {
                // 20261104 [iOS] A정책 - 조건 없는 onResumeDocument(무조건 play) 대신, 다른 앱 소리 상태를 보고 자동 재생/터치 안내
                //          (앱이 복귀 시 보내는 pro-otherAudioState 로도 판단하고, 그 메시지가 없을 때를 대비해 1초 뒤 한 번 더 확인)
                console.log("[iOS] LiveMediaSoupRcv - 화면 복귀");
                setTimeout(() => { if (tryResumeTeacherIOSRef.current) tryResumeTeacherIOSRef.current("visible+1s"); }, 1000);
            }
        };
        // 20261104 [iOS] 앱이 알려주는 다른 앱 소리 상태 (pro-otherAudioState → HandsUpClient → 이 이벤트)
        const onOtherAudioIOS = (e) => {
            const busy = !!(e && e.detail && e.detail.otherAudioBusy);
            const changed = otherAudioBusyRef.current !== busy;
            otherAudioBusyRef.current = busy;
            console.log(`[iOS] 다른 앱 소리 상태 busy[${busy}] reason[${e && e.detail && e.detail.reason}]`);
            // 20261104 [iOS] A정책 - 다른 앱 소리 사용 중엔 선생님 소리 <audio> 자동재생을 꺼서 재수신 때 다른 앱 소리를 끊지 않음
            if (audioEl.current) audioEl.current.autoplay = !busy;
            if (tryResumeTeacherIOSRef.current && (changed || !busy)) tryResumeTeacherIOSRef.current("otherAudio:" + (e && e.detail && e.detail.reason));
        };
        // 20261104 [iOS] 다른 앱 소리 사용 중이라 선생님 소리 재생이 보류됨(MediaSoupClient.playAudio) → 안내 판단
        const onTeacherAudioDeferredIOS = () => {
            setTimeout(() => { if (tryResumeTeacherIOSRef.current) tryResumeTeacherIOSRef.current("audioDeferred"); }, 300);
        };
        if (isIOS) {
            document.addEventListener("visibilitychange", onVisibleIOS);
            window.addEventListener("pageshow", onVisibleIOS);
            window.addEventListener("hiclass-ios-other-audio", onOtherAudioIOS); // 20261104 [iOS]
            window.addEventListener("hiclass-ios-teacher-audio-deferred", onTeacherAudioDeferredIOS); // 20261104 [iOS]
        }

        return () => {
            document.removeEventListener("freeze", onFreezeDocument);
            document.removeEventListener("resume", onResumeDocument);
            if (isIOS) { // 20261103 [iOS]
                document.removeEventListener("visibilitychange", onVisibleIOS);
                window.removeEventListener("pageshow", onVisibleIOS);
                window.removeEventListener("hiclass-ios-other-audio", onOtherAudioIOS); // 20261104 [iOS]
                window.removeEventListener("hiclass-ios-teacher-audio-deferred", onTeacherAudioDeferredIOS); // 20261104 [iOS]
            }
        };
    }, []); // ref만 사용하므로 deps 불필요, 마운트 시 1회 등록

    // 20261103 [iOS] 화면이 보이는 상태에서 선생님 영상이 저절로 멈추면(유튜브 PiP 등 다른 앱 소리 인터럽트)
    // 자동 play() 는 하지 않음 - 다른 앱과 소리를 서로 뺏으며 재생/멈춤이 반복됨(유튜브 PiP 실기기 확인)
    // → '터치하면 다시 재생' 안내만 표시하고, 사용자가 터치하면 다시 재생 (연결이 끊긴 경우는 기존 재연결)
    // 20261104 [iOS] 선생님 영상이 멈춰 있을 때 판단 하나로 통일
    // - 다른 앱이 소리 사용 중(유튜브 PiP 재생 등) → 자동 재생하지 않고 '터치하면 다시 재생' 안내
    // - 다른 앱 소리 없음 → 자동 play() (선생님 소리 포함, 5초에 1번만), 실패하면 안내
    const tryResumeTeacherIOS = (reason) => {
        if (!isIOSWebView()) return;
        const v = videoEl.current;
        if (!v || document.visibilityState !== "visible") return; // 숨김 중엔 화면 복귀 때 처리
        const track = v.srcObject && v.srcObject.getVideoTracks && v.srcObject.getVideoTracks()[0];
        if (!track || track.readyState !== "live") return; // 연결 끊김은 기존 재연결 로직(liveReconnectState)에 맡김
        // 20261104 [iOS] 선생님 소리(<audio>)가 멈춘 경우도 포함 (영상은 재생 중인데 소리만 보류/멈춤)
        const pausedAudios = [audioEl.current, micEl.current].filter((a) => a && a.srcObject && a.paused);
        if (!v.paused && pausedAudios.length === 0) { setTeacherPausedIOS(false); return; }
        if (otherAudioBusyRef.current) {
            console.log(`[iOS] 선생님 화면/소리 멈춤(${reason}) - 다른 앱 소리 사용 중 → 터치 재생 안내`);
            setTeacherPausedIOS(true);
            return;
        }
        // 20261104 [iOS] A정책 - 화면이 보이는 중에 '멈추는 순간'은 다른 앱이 소리를 가져간 것 → 자동 재생하지 않음(앱 상태 알림보다 먼저 와서 유튜브를 다시 끊던 문제)
        if (reason === "pause") {
            console.log("[iOS] 선생님 화면 멈춤(pause) → 터치 재생 안내 (자동 재생 안 함)");
            setTeacherPausedIOS(true);
            return;
        }
        const now = Date.now();
        if (now - lastAutoPlayIOSRef.current < 5000) { setTeacherPausedIOS(true); return; } // 5초 안에 다시 멈춤 → 반복 방지, 안내만
        lastAutoPlayIOSRef.current = now;
        console.log(`[iOS] 선생님 화면/소리 멈춤(${reason}) - 다른 앱 소리 없음 → 자동 play()`);
        if (v.paused) {
            v.play().then(() => setTeacherPausedIOS(false)).catch((err) => {
                console.warn("[iOS] teacher video 자동 play 실패 → 터치 재생 안내:", err);
                setTeacherPausedIOS(true);
            });
        } else {
            setTeacherPausedIOS(false);
        }
        pausedAudios.forEach((a) => { // 선생님 소리도 멈춰 있으면 같이 재생
            a.autoplay = true; // 20261104 다른 앱 소리 사용 중 꺼뒀던 자동재생 복원
            a.play().catch((err) => console.warn("[iOS] teacher audio 자동 play 실패:", err));
        });
    };
    const tryResumeTeacherIOSRef = useRef(null); // 20261104 [iOS] useEffect([]) 안의 이벤트에서 최신 함수 호출용
    tryResumeTeacherIOSRef.current = tryResumeTeacherIOS;

    const onTeacherVideoPauseIOS = () => { // 20261103 [iOS] → 20261104 다른 앱 소리 상태에 따라 판단
        if (!isIOSWebView()) return;
        tryResumeTeacherIOS("pause");
    };
    const onTeacherVideoCanPlayIOS = () => { // 20261104 [iOS] 재연결 등으로 새 영상이 붙었는데 멈춰 있으면 판단
        if (!isIOSWebView()) return;
        setTimeout(() => tryResumeTeacherIOS("canplay"), 300);
    };
    const onTeacherVideoPlayingIOS = () => { // 20261103 [iOS]
        if (!isIOSWebView()) return;
        tryResumeTeacherIOS("playing"); // 20261104 영상은 재생돼도 선생님 소리가 보류 중이면 안내 유지
    };
    const onTapResumeTeacherIOS = (e) => { // 20261103 [iOS] 안내 터치 → 다시 재생(사용자 동작이라 WebKit 이 허용)
        if (e) e.stopPropagation();
        if (!isIOSWebView()) return;
        const v = videoEl.current;
        const track = v && v.srcObject && v.srcObject.getVideoTracks && v.srcObject.getVideoTracks()[0];
        if (!v || !track || track.readyState !== "live") {
            console.log("[iOS] 터치 재생 - 영상 트랙 없음/종료 → 재연결 시도");
            setTeacherPausedIOS(false);
            retryLiveReconnect();
            return;
        }
        console.log("[iOS] 터치 재생 - 선생님 영상/소리 play()");
        v.play().then(() => setTeacherPausedIOS(false)).catch((err) => {
            console.warn("[iOS] teacher video play 실패:", err);
            addMediaError({ roomKind: "live", mediaKind: "video", prodUserSeq: teacherInfoRef.current.userSeq, errorCode: "PLAYBACK_FAILED" });
        });
        [audioEl.current, micEl.current].forEach((a) => { // 선생님 소리도 멈춰 있으면 같이 재생
            if (a && a.srcObject && a.paused) { a.autoplay = true; a.play().catch((err) => console.warn("[iOS] teacher audio play 실패:", err)); } // 20261104 자동재생 복원
        });
    };

    const onHandleScreenMode = (kind) => {        
        if (lDrawingMode.current !== enDrawingMode.Hand) return;

        if (!kind || kind === "") return;
        if (kind === "teacher" && videoEl.current === undefined) return;
        if (kind === "myCam" && myCamVideoEl.current === undefined) return;

        if (kind === "teacher" && videoEl.current && videoEl.current.paused) return;
        if (kind === "myCam" && myCamVideoEl.current && myCamVideoEl.current.paused) return;

        // 1. teacher - small / myCam - small
        // 2. teacher - full / myCam - small
        // 3. teacher - small / myCam - full
        // 4. teacher - full / myCam - small

        if (kind === "teacher") {
            if (!isMyCamFullScreenMode) {
                handleControlToolFunc({ target: "isFullScreenMode", value: !isFullScreenMode });
            } else {
                setIsMyCamFullScreenMode(!isMyCamFullScreenMode);
            }
        } else if (kind === "myCam") {
            setIsMyCamFullScreenMode(!isMyCamFullScreenMode);
        }
    }

    const onHandlePreviewControl = (e, selectedFunc) => {
        e.preventDefault();
        e.stopPropagation();
        handleSelectedLiveFunc({ selectedFunc });
    }

    return (
        <div className="live-area mediasoup">
            {
                performLiveInfo.handsUpYN === "Y" &&
                <div id="student-area" ref={handsUpAreaRef} style={{ position: 'absolute', width: `${lHandsUpWidth}`, height: `${lHandsUpHeight}`, visibility: `${teacherViewVisibility}`, right: `${lHandsUpLeftOffset}`, margin: `${isMyCamFullScreenMode ? '0px' : '2px'}`, zIndex: `${isFullScreenMode ? 46 : 45/* 1 : 'unset' */}` }} onClick={() => onHandleScreenMode('myCam')}>
                    <video id="handsup_video-comp" ref={handsupVideoEl} className="mediasoup-comp handsup" style={{ display: `${lHandsUpVideoDisplayMode}` }} playsInline autoPlay muted />
                    <video id="handsup_myCam-comp" ref={myCamVideoEl} className={"mediasoup-comp handsup" + (isMyCamFullScreenMode ? " b-none" : "")} style={{ display: `${myCamViewVisibility}` }} playsInline autoPlay muted />
                    {/* {
                        myCamViewVisibility === "none" &&
                        <img id="handsup_img-comp" ref={handsupImgEl} className="mediasoup-comp" style={{ display: `${lHandsUpImgDisplayMode}` }} src="/images/connect.png" alt="" />
                    } */}
                    <img id="handsup_img-comp" ref={handsupImgEl} className="mediasoup-comp" style={{ display: `${lHandsUpImgDisplayMode}` }} src="/images/connect.png" alt="" />
                    <div id="handsAudioConsumeDiv" style={{ display: 'none' }} />
                </div>
            }
            <div className="hc-teacher-preview-shell" style={{ position: 'relative', width: `${lWidth}`, height: `${lHeight}`, visibility: `${teacherViewVisibility}`, margin: `${isFullScreenMode ? '0px' : '2px'}`, zIndex: `${isMyCamFullScreenMode ? 46 : 45/* 1 : 'unset' */}` }}>
            <div id="teacher-area" ref={teacherViewAreaRef} style={{ position: 'relative', width: `${lWidth}`, height: `${lHeight}`, visibility: 'inherit', margin: '0px'/* , border: performLiveStatusInfo.isConnect ? '2px solid rgb(75, 145, 215)' : 'none' */ }} onClick={() => onHandleScreenMode("teacher")}>
                {
                    performLiveStatusInfo.isConnect ?
                        <VideoComp id="mediasoup-video-comp" ref={videoEl} className={"mediasoup-comp teacher" /* + (isFullScreenMode ? " b-none" : "") */} /*style={{ visibility: `${teacherViewVisibility}` }}*/ playsInline autoPlay onPause={isIOSWebView() ? onTeacherVideoPauseIOS : undefined} onPlaying={isIOSWebView() ? onTeacherVideoPlayingIOS : undefined} onCanPlay={isIOSWebView() ? onTeacherVideoCanPlayIOS : undefined} /* 20261103~20261104 [iOS] 자동 멈춤 복구 */ /> :
                        <ConnectImgComp id="mediasoup-img-comp" className="mediasoup-comp teacher" /*style={{ visibility: `${teacherViewVisibility}` }}*/ src="/images/connect.png" alt="" />
                }
                {/* <div style={{ background: 'rgba(255, 255, 255, .4)' }}>
                    <span>선생님 화면</span>
                </div> */}
                <span className="mediasoup-comp teacher" style={{ visibility: `${teacherViewVisibility}` }}>{t("진행자_화면", { ns: "live" })}</span>
                {
                    // 20260929 선생님 영상 연결이 끊겼을 때 안내 (자동 재연결 중 / 자동 재연결 실패 시 [다시 연결])
                    liveReconnectState !== "idle" &&
                    <div id="liveReconnectOverlay" style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.72)', color: '#fff', textAlign: 'center', padding: '4px', boxSizing: 'border-box', fontSize: '12px', lineHeight: 1.35 }} onClick={(e) => e.stopPropagation()}>
                        {
                            liveReconnectState === "failed" ?
                                <>
                                    <div>{t("선생님_영상_연결_끊김", { ns: "live", defaultValue: "선생님 영상 연결이 끊겼습니다" })}</div>
                                    <button type="button" style={{ marginTop: '6px', display: 'flex', alignItems: 'center', padding: '5px 12px', border: 0, borderRadius: '6px', background: '#e5484d', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(229, 72, 77, 0.35)' }} onClick={onClickLiveReconnect}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" style={{ marginRight: '5px' }}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>
                                        {t("다시_연결", { ns: "live", defaultValue: "다시 연결" })}
                                    </button>
                                </> :
                                <div>{t("선생님_영상_재연결_중", { ns: "live", defaultValue: "선생님 영상 재연결 중…" })}</div>
                        }
                    </div>
                }
                {
                    // 20261103 [iOS] 선생님 영상이 다른 앱 소리(유튜브 PiP 등)로 멈췄을 때 '터치하면 다시 재생' 안내 (재연결 안내가 없을 때만)
                    isIOSWebView() && teacherPausedIOS && liveReconnectState === "idle" &&
                    <div id="teacherPausedOverlayIOS" style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.72)', color: '#fff', textAlign: 'center', padding: '4px', boxSizing: 'border-box', fontSize: '12px', lineHeight: 1.35, cursor: 'pointer' }} onClick={onTapResumeTeacherIOS}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style={{ marginBottom: '4px' }}><path d="M8 5v14l11-7z" /></svg>
                        <div>{t("선생님_화면_일시정지", { ns: "live", defaultValue: "선생님 화면이 일시정지되었습니다" })}</div>
                        <div>{t("터치하면_다시_재생", { ns: "live", defaultValue: "터치하면 다시 재생합니다" })}</div>
                    </div>
                }
                <audio id="audio-comp" ref={audioEl} style={{ display: 'none' }} autoPlay />
                <audio id="mic-comp" ref={micEl} style={{ display: 'none' }} />
            </div>
            <div className="hc-teacher-preview-controls hc-teacher-preview-controls-strip" style={{ width: `${lWidth}`, top: `calc(${lHeight} + ${isFullScreenMode ? '8px' : '10px'})` }}>
                <button type="button" className="hc-teacher-preview-control" disabled={performHandsUpStatusInfo.micStatus === "disable" || performHandsUpStatusInfo.micStatus === "muteDisable"} onClick={(e) => onHandlePreviewControl(e, "PROC_MIC")}>
                    <img src={performHandsUpStatusInfo.micStatus === "disable" || performHandsUpStatusInfo.micStatus === "off" ? "/images/liveTool/micOffDisable.png" : performHandsUpStatusInfo.micStatus === "mute" ? "/images/liveTool/micOn.png" : "/images/liveTool/micOnDisable.png"} alt="" />
                </button>
                <button type="button" className="hc-teacher-preview-control" disabled={(performLiveInfo.handsUpYN !== "Y" && performHandsUpStatusInfo.connectStatus === "disable")} onClick={(e) => onHandlePreviewControl(e, "PROC_CONN")}>
                    <img src={performHandsUpStatusInfo.connectStatus === "disable" ? "/images/liveTool/disconnectDisable.png" : performHandsUpStatusInfo.connectStatus === "disconnect" ? "/images/liveTool/disconnect.png" : "/images/liveTool/connectAble.png"} alt="" />
                </button>
                <button type="button" className="hc-teacher-preview-control" disabled={performLiveInfo.handsUpYN !== "Y" || isIOSWebView()} onClick={(e) => onHandlePreviewControl(e, "CHANGE_VIEW_KIND")}>
                    <img src={(performLiveInfo.liveSeq === -1 || performLiveInfo.handsUpYN === "N" || isIOSWebView()) ? "/images/liveTool/transCamDisable.png" : viewKind === "CAMERA" ? "/images/liveTool/transCam.png" : "/images/liveTool/screenMode.png"} alt="" />
                </button>
            </div>
            </div>
            {
                performLiveInfo.handsUpYN === "Y" && smallGroupInfo.status &&
                <LiveSmallGroupWindow
                    userSeq={userSeq}
                    performLiveSeq={performLiveInfo.liveSeq}
                    smallGroupInfo={smallGroupInfo}
                    visibility={lHandsUpVideoDisplayMode}
                    isRbmqServerConnected={isRbmqServerConnected}
                    isReadyLiveConsume={isReadyLiveConsume}
                />
            }
        </div>
    );
}

export default LiveMediaSoupRcv;
